import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import BlurUpImage from '../components/BlurUpImage';
import { useStore } from '../store/useStore';
import { useAuthStore } from '../store/useAuthStore';
import { useUIStore } from '../store/useUIStore';
import { getCountryFromIP, getDefaultCurrency } from '../services/geolocationService';
import { CURRENCIES } from '../constants';
import { useCurrency } from '../hooks/useCurrency';
import '../styles/Home.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export default function Home() {
  const navigate = useNavigate();
  const products = useStore((state) => state.products);
  const setSelectedCurrency = useStore((state) => state.setSelectedCurrency);
  const token = useAuthStore((state) => state.token);
  const showMobileMenu = useUIStore((state) => state.showMobileMenu);
  const { convertPrice, currencySymbol, selectedCurrency } = useCurrency();
  const [currentBottomImageIndex, setCurrentBottomImageIndex] = useState(0);
  const [bottomImages, setBottomImages] = useState([]);
  const [heroImage, setHeroImage] = useState(null);
  const [touchStartX, setTouchStartX] = useState(0);
  const [slideOffset, setSlideOffset] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  // Featured products carousel
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [currentFeaturedIndex, setCurrentFeaturedIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

  // Products page filters
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [sortBy] = useState('newest');
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [categories, setCategories] = useState(['Mens', 'Womens', 'Kids', 'Sports']);
  const [catalogMinPrice, setCatalogMinPrice] = useState(0);
  const [catalogMaxPrice, setCatalogMaxPrice] = useState(500);
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(500);
  const [containerWidth, setContainerWidth] = useState(0);

  const fetchHeroImage = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/hero-banner/top`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        setHeroImage(data.image_url || null);
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
    }
  }, [token]);

  const fetchBottomImages = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/hero-banner/bottom`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Bottom hero images fetched:', data);
        setBottomImages(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching bottom hero images:', error);
    }
  }, [token]);

  // Fetch featured products
  const fetchFeaturedProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/featured-products`);
      if (response.ok) {
        const data = await response.json();
        // Ensure featured products have correct id field for navigation
        const mappedData = Array.isArray(data) ? data.map(product => {
          const productId = product.id || product.product_id;
          // Try to enrich with full product data from main products list
          const fullProduct = products.find(p => p.id === productId);
          return {
            ...fullProduct || product,
            id: productId
          };
        }) : [];
        setFeaturedProducts(mappedData);
      }
    } catch (error) {
      console.error('Error fetching featured products:', error);
      setFeaturedProducts([]);
    }
  }, [products]);

  // Handle window resize for responsive carousel
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Detect country and set default currency
  useEffect(() => {
    const detectCurrency = async () => {
      const countryCode = await getCountryFromIP();
      const currency = getDefaultCurrency(countryCode);
      setSelectedCurrency(currency);
    };
    detectCurrency();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load categories from API and poll for updates
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          // Extract just the names from the category objects
          const categoryNames = data.map(cat => cat.name);
          if (Array.isArray(categoryNames) && categoryNames.length > 0) {
            setCategories(categoryNames);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fall back to defaults if API fails
        setCategories(['Mens', 'Womens', 'Kids', 'Sports']);
      }
    };

    // Fetch on mount
    fetchCategories();
  }, []);

  // Helper function to round down to whole fifths
  const roundDownToFifth = useCallback((price) => {
    return Math.floor(price / 5) * 5;
  }, []);

  // Wrap convertPrice to include rounding logic
  const convertPriceRounded = useCallback((priceUSD) => {
    const converted = parseFloat(convertPrice(priceUSD));
    return roundDownToFifth(converted).toFixed(2);
  }, [convertPrice, roundDownToFifth]);

  // Get unique colors from products
  const availableColors = useMemo(() => {
    const colorMap = new Map();
    products.forEach(product => {
      if (product.colors) {
        product.colors.forEach(color => {
          // Use hex as unique key since color.id might not be unique
          const colorKey = color.color_hex || color.id;
          if (!colorMap.has(colorKey)) {
            colorMap.set(colorKey, { id: color.id, name: color.color_name, hex: color.color_hex });
          }
        });
      }
    });
    return Array.from(colorMap.values());
  }, [products]);

  // Get unique sizes from products
  const availableSizes = useMemo(() => {
    const sizeSet = new Set();
    products.forEach(product => {
      // Extract sizes from stockByColor: { [colorId]: { [size]: quantity } }
      if (product.stockByColor) {
        Object.values(product.stockByColor).forEach(colorStock => {
          if (colorStock && typeof colorStock === 'object') {
            Object.keys(colorStock).forEach(size => {
              sizeSet.add(size);
            });
          }
        });
      }
    });

    // Sort sizes in ascending order
    const sortedSizes = Array.from(sizeSet).sort((a, b) => parseFloat(a) - parseFloat(b));

    // Reorder for column-first display (top-to-bottom, then next column)
    const reorderForColumns = (arr, numColumns = 2) => {
      const itemsPerColumn = Math.ceil(arr.length / numColumns);
      const result = [];

      for (let i = 0; i < itemsPerColumn; i++) {
        for (let col = 0; col < numColumns; col++) {
          const index = col * itemsPerColumn + i;
          if (index < arr.length) {
            result.push(arr[index]);
          }
        }
      }

      return result;
    };

    return reorderForColumns(sortedSizes);
  }, [products]);

  // Calculate min and max prices from catalog
  useEffect(() => {
    if (products.length > 0) {
      const prices = products.map(p => parseFloat(p.price));
      const minPrice = Math.floor(Math.min(...prices));
      const maxPrice = Math.ceil(Math.max(...prices));
      setCatalogMinPrice(minPrice);
      setCatalogMaxPrice(maxPrice);
      setMinPrice(minPrice);
      setMaxPrice(maxPrice);
    }
  }, [products]);

  useEffect(() => {
    fetchHeroImage();
    fetchBottomImages();
    fetchFeaturedProducts();
  }, [fetchHeroImage, fetchBottomImages, fetchFeaturedProducts]);

  // Bottom slideshow auto-rotate
  useEffect(() => {
    if (bottomImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentBottomImageIndex((prev) => (prev + 1) % bottomImages.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [bottomImages.length]);

  // Products page filtering
  useEffect(() => {
    let filtered = products;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(
        (product) => product.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    // Filter by price range
    filtered = filtered.filter(
      (product) => parseFloat(product.price) >= minPrice && parseFloat(product.price) <= maxPrice
    );

    // Filter by colors
    if (selectedColors.length > 0) {
      filtered = filtered.filter((product) => {
        if (!product.colors) return false;
        return selectedColors.some(colorId =>
          product.colors.some(c => c.id === colorId)
        );
      });
    }

    // Filter by sizes
    if (selectedSizes.length > 0) {
      filtered = filtered.filter((product) => {
        if (!product.stockByColor) return false;
        // Check if any of the selected sizes exist in any color for this product
        return selectedSizes.some(size => {
          return Object.values(product.stockByColor).some(colorStock => {
            return colorStock && typeof colorStock === 'object' && size in colorStock;
          });
        });
      });
    }

    // Sort
    if (sortBy === 'price-low') {
      filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name));
    }

    setFilteredProducts(filtered);
  }, [products, selectedCategory, minPrice, maxPrice, selectedColors, selectedSizes, sortBy, selectedCurrency]);

  // Calculate items per view based on window width
  const itemsPerView = useMemo(() => {
    if (windowWidth <= 500) {
      return 1; // Mobile: one at a time
    } else if (windowWidth < 1025) {
      return 2; // Tablet: two at a time
    } else {
      return 4; // Desktop: four at a time
    }
  }, [windowWidth]);

  // Calculate the transform percentage per item
  const getCarouselTransform = useMemo(() => {
    return currentFeaturedIndex * (100 / itemsPerView);
  }, [currentFeaturedIndex, itemsPerView]);

  // Featured products carousel navigation - responsive items per view
  const scrollFeaturedCarousel = (direction) => {
    // Only allow scrolling if there are more items than can be displayed at once
    if (featuredProducts.length <= itemsPerView) {
      return;
    }

    const maxScrollIndex = Math.max(0, featuredProducts.length - itemsPerView);

    if (direction === 'next') {
      setCurrentFeaturedIndex((prev) => prev < maxScrollIndex ? prev + 1 : 0);
    } else {
      setCurrentFeaturedIndex((prev) => prev > 0 ? prev - 1 : maxScrollIndex);
    }
  };

  const toggleColor = (colorId) => {
    setSelectedColors(prev =>
      prev.includes(colorId)
        ? prev.filter(id => id !== colorId)
        : [...prev, colorId]
    );
  };

  const toggleSize = (size) => {
    setSelectedSizes(prev =>
      prev.includes(size)
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  // Get container width for swipe calculations
  useEffect(() => {
    const container = document.querySelector('.hero-bottom-slideshow');
    if (container) {
      setContainerWidth(container.clientWidth);
    }

    // Also update on window resize
    const handleResize = () => {
      const container = document.querySelector('.hero-bottom-slideshow');
      if (container) {
        setContainerWidth(container.clientWidth);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Swipe handlers for bottom hero slideshow
  const handleTouchStart = (e) => {
    // Only allow swiping if menu is not open
    if (showMobileMenu) return;
    if (e.touches.length > 0) {
      setTouchStartX(e.touches[0].clientX);
      setSlideOffset(0);
      setIsSliding(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isSliding || showMobileMenu || !containerWidth) return;
    const currentX = e.touches[0].clientX;
    const diff = currentX - touchStartX;
    setSlideOffset(diff);
  };

  const handleTouchEnd = () => {
    setIsSliding(false);
    // Don't process swipe if menu is open
    if (showMobileMenu) {
      setSlideOffset(0);
      return;
    }

    if (!containerWidth) {
      setSlideOffset(0);
      return;
    }

    // Threshold is 20% of container width or 50px, whichever is smaller
    const threshold = Math.min(containerWidth * 0.2, 50);

    if (bottomImages.length > 0) {
      // slideOffset positive = swiped right (go to previous)
      // slideOffset negative = swiped left (go to next)
      if (slideOffset > threshold) {
        // Swiped right - go to previous image
        setCurrentBottomImageIndex((prev) => (prev - 1 + bottomImages.length) % bottomImages.length);
      } else if (slideOffset < -threshold) {
        // Swiped left - go to next image
        setCurrentBottomImageIndex((prev) => (prev + 1) % bottomImages.length);
      }
    }
    setSlideOffset(0);
  };

  return (
    <div className="home-page">
      {/* Hero Section - Static Image */}
      {heroImage && (
        <section className="hero">
          <BlurUpImage
            src={heroImage}
            alt="Shoes"
            className="hero-image"
            objectFit="cover"
            objectPosition="center"
          />
        </section>
      )}

      {/* Featured Products Carousel */}
      {featuredProducts.length > 0 && (
        <section className="featured-section">
          <div className="featured-container">
            <h2 className="featured-title">Featured Products</h2>
            <div className="featured-carousel">
              <button
                className="carousel-btn prev"
                onClick={() => scrollFeaturedCarousel('prev')}
                title="Previous"
              >
                ‹
              </button>
              <div className="featured-items-wrapper">
                <div className="featured-items" style={{ transform: `translateX(-${getCarouselTransform}%)` }}>
                  {featuredProducts.map((product) => (
                    <div key={product.id} className="featured-item">
                      <ProductCard
                        product={product}
                        selectedCurrency={selectedCurrency}
                        convertPrice={convertPriceRounded}
                        currencySymbol={CURRENCIES[selectedCurrency].symbol}
                        isFeatured={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <button
                className="carousel-btn next"
                onClick={() => scrollFeaturedCarousel('next')}
                title="Next"
              >
                ›
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Products Section with Filters */}
      <section className="products-section">
        <div className="home-container">
          <div className="products-container">
            {/* Filter Panel - Slides from Left with All Filters */}
            {expandedFilter && (
              <>
                <div
                  className="filter-panel-overlay"
                  onClick={() => setExpandedFilter(null)}
                />
                <div className="filter-panel">
                  <div className="filter-panel-header">
                    <h3>Filters</h3>
                    <button
                      className="filter-panel-close"
                      onClick={() => setExpandedFilter(null)}
                    >
                      ×
                    </button>
                  </div>

                  <div className="filter-panel-content">
                    {/* Categories Section */}
                    <div className="filter-section">
                      <h4>Categories</h4>
                      <div className="filter-grid">
                        <button
                          key="all"
                          className={`filter-grid-item ${selectedCategory === 'all' ? 'active' : ''}`}
                          onClick={() => setSelectedCategory('all')}
                        >
                          All
                        </button>
                        {categories.map((cat) => (
                          <button
                            key={cat.toLowerCase()}
                            className={`filter-grid-item ${selectedCategory === cat.toLowerCase() ? 'active' : ''}`}
                            onClick={() => setSelectedCategory(cat.toLowerCase())}
                          >
                            {cat}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Price Section */}
                    <div className="filter-section">
                      <h4>Price Range</h4>
                      <div className="price-filter">
                        <div className="price-display">
                          <span>{currencySymbol}{convertPriceRounded(minPrice)}</span>
                          <span>−</span>
                          <span>{currencySymbol}{convertPriceRounded(maxPrice)}</span>
                        </div>
                        <div className="slider-container">
                          <input
                            type="range"
                            min={catalogMinPrice}
                            max={catalogMaxPrice}
                            value={minPrice}
                            onChange={(e) => {
                              const val = Math.min(parseInt(e.target.value) || catalogMinPrice, maxPrice - 1);
                              setMinPrice(val);
                            }}
                            className="slider min-slider"
                          />
                          <input
                            type="range"
                            min={catalogMinPrice}
                            max={catalogMaxPrice}
                            value={maxPrice}
                            onChange={(e) => {
                              const val = Math.max(parseInt(e.target.value) || catalogMaxPrice, minPrice + 1);
                              setMaxPrice(val);
                            }}
                            className="slider max-slider"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Color Section */}
                    {availableColors.length > 0 && (
                      <div className="filter-section">
                        <h4>Color</h4>
                        <div className="filter-grid color-grid">
                          {availableColors.map((color) => (
                            <button
                              key={color.id}
                              className={`filter-grid-item color-item ${selectedColors.includes(color.id) ? 'active' : ''}`}
                              onClick={() => toggleColor(color.id)}
                              title={color.name}
                            >
                              <span
                                className="color-swatch"
                                style={{ backgroundColor: color.hex }}
                              />
                              <span>{color.name}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Size Section */}
                    <div className="filter-section">
                      <h4>Size (EU)</h4>
                      <div className="filter-grid">
                        {availableSizes.map((size) => (
                          <button
                            key={size}
                            className={`filter-grid-item ${selectedSizes.includes(size) ? 'active' : ''}`}
                            onClick={() => toggleSize(size)}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Main Products Content */}
            <div className="products-main">
              {/* Header Controls */}
              <div className="products-header">
                <div className="header-controls">
                  {/* Filter Button - Left */}
                  <button
                    className={`filter-btn ${expandedFilter ? 'active' : ''}`}
                    onClick={() => setExpandedFilter(expandedFilter ? null : 'open')}
                  >
                    Filter By
                  </button>

                  {/* Currency Selector - Center */}
                  <div className="currency-controls">
                    <select
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                      className="currency-select"
                      title="Select currency"
                    >
                      {Object.entries(CURRENCIES).map(([code, { symbol, name }]) => (
                        <option key={code} value={code}>
                          {symbol} {name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              {filteredProducts.length > 0 ? (
                <div className="products-grid">
                  {filteredProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      selectedCurrency={selectedCurrency}
                      convertPrice={convertPrice}
                      currencySymbol={CURRENCIES[selectedCurrency].symbol}
                    />
                  ))}
                </div>
              ) : (
                <div className="no-products-message">
                  <p>No products found. Try adjusting your filters.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Hero Banner - Slideshow */}
      {bottomImages.length > 0 && (
        <section className="hero-bottom">
          <div
            className="hero-bottom-slideshow"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              style={{
                display: 'flex',
                transform: `translateX(${-currentBottomImageIndex * 100 + (slideOffset / (containerWidth || 1)) * 100}%)`,
                transition: isSliding ? 'none' : 'transform 0.3s ease-out'
              }}
            >
              {bottomImages.map((image, idx) => {
                const isActive = idx === currentBottomImageIndex;
                const isClickable = image.product_id;
                return (
                  <div
                    key={image.id || idx}
                    className={`hero-bottom-image-wrapper ${isActive ? 'active' : ''} ${isClickable ? 'clickable' : ''}`}
                    onClick={() => isClickable && navigate(`/product/${image.product_id}`)}
                    role={isClickable ? 'button' : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyPress={(e) => isClickable && (e.key === 'Enter' || e.key === ' ') && navigate(`/product/${image.product_id}`)}
                    style={{
                      minWidth: '100%',
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={image.image_url || ''}
                      alt={`Hero ${idx + 1}`}
                      className="hero-bottom-image"
                      onLoad={() => console.log(`Image ${idx + 1} loaded successfully:`, image.image_url)}
                      onError={(e) => {
                        console.error(`Image ${idx + 1} failed to load!`);
                        console.error('  Image object:', image);
                        console.error('  URL:', image.image_url);
                        console.error('  Error event:', e);
                      }}
                    />
                    {isActive && isClickable && (
                      <div className="hero-overlay">
                        <span className="hero-cta">View Product</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {bottomImages.length > 1 && (
            <div className="hero-bottom-indicators">
              {bottomImages.map((_, idx) => (
                <button
                  key={idx}
                  className={`indicator ${idx === currentBottomImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentBottomImageIndex(idx)}
                  title={`View image ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
